import { supabase } from "@/integrations/supabase/client";

export interface LLMConfig {
  id: string;
  brandId: string;
  source: string; // Display name (e.g., "5Ws Framework")
  technicalSource?: string; // Technical name used in code (e.g., "five_ws_analysis")
  provider: 'openai' | 'gemini' | 'lovable' | 'claude' | 'perplexity';
  model: string;
  isActive: boolean;
  priority: number;
  metadata: {
    temperature?: number;
    max_tokens?: number;
    maxOutputTokens?: number;
    [key: string]: any;
  };
  createdAt: string;
  updatedAt: string;
}

export interface LLMConfigInput {
  id?: string;
  brandId: string;
  source: string; // Display name (e.g., "5Ws Framework")
  technicalSource?: string; // Technical name used in code (e.g., "five_ws_analysis")
  provider: 'openai' | 'gemini' | 'lovable' | 'claude' | 'perplexity';
  model: string;
  isActive?: boolean;
  priority?: number;
  metadata?: {
    temperature?: number;
    max_tokens?: number;
    maxOutputTokens?: number;
    [key: string]: any;
  };
}

export class LLMConfigService {
  /**
   * Get the active LLM configuration for a specific source
   * Returns the highest priority (lowest number) active config, or null if none found
   */
  static async getLLMConfig(brandId: string, source: string): Promise<LLMConfig | null> {
    try {
      console.log(`[LLMConfigService] Looking up config for brandId="${brandId}", source="${source}"`);
      
      // Look up by technical_source first (the code uses technical names)
      // If not found, fall back to source (display name) for backward compatibility
      let { data, error } = await supabase
        .from('llm_source_configurations')
        .select('*')
        .eq('brand_id', brandId)
        .eq('is_active', true)
        .or(`technical_source.eq.${source},source.eq.${source}`)
        .order('priority', { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) {
        console.error('[LLMConfigService] ❌ Error fetching config:', error);
        return null;
      }

      if (!data) {
        console.log(`[LLMConfigService] ⚠️ No active config found for brandId="${brandId}", source="${source}"`);
        return null;
      }

      const transformed = this.transformToLLMConfig(data);
      console.log(`[LLMConfigService] ✅ Found active config:`, {
        provider: transformed.provider,
        model: transformed.model,
        isActive: transformed.isActive,
        priority: transformed.priority
      });
      return transformed;
    } catch (error) {
      console.error('[LLMConfigService] ❌ Error in getLLMConfig:', error);
      return null;
    }
  }

  /**
   * Get all LLM configurations for a brand, optionally filtered by source
   */
  static async getLLMConfigs(brandId: string, source?: string): Promise<LLMConfig[]> {
    try {
      let query = supabase
        .from('llm_source_configurations')
        .select('*')
        .eq('brand_id', brandId);

      if (source) {
        query = query.eq('source', source);
      }

      query = query.order('source', { ascending: true })
                   .order('priority', { ascending: true });

      const { data, error } = await query;

      if (error) {
        console.error('LLMConfigService: Error fetching configs:', error);
        return [];
      }

      if (!data) {
        return [];
      }

      return data.map(item => this.transformToLLMConfig(item));
    } catch (error) {
      console.error('LLMConfigService: Error in getLLMConfigs:', error);
      return [];
    }
  }

  /**
   * Save or update an LLM configuration
   */
  static async saveLLMConfig(config: LLMConfigInput): Promise<LLMConfig> {
    try {
      const dbConfig: any = {
        brand_id: config.brandId,
        source: config.source,
        technical_source: config.technicalSource || null,
        provider: config.provider,
        model: config.model,
        is_active: config.isActive ?? true,
        priority: config.priority ?? 0,
        metadata: config.metadata || {}
      };

      if (config.id) {
        // Update existing
        const { data, error } = await supabase
          .from('llm_source_configurations')
          .update(dbConfig)
          .eq('id', config.id)
          .select()
          .single();

        if (error) {
          throw new Error(`Failed to update LLM config: ${error.message}`);
        }

        if (!data) {
          throw new Error('No data returned after update');
        }

        return this.transformToLLMConfig(data);
      } else {
        // Insert new
        const { data, error } = await supabase
          .from('llm_source_configurations')
          .insert(dbConfig)
          .select()
          .single();

        if (error) {
          throw new Error(`Failed to create LLM config: ${error.message}`);
        }

        if (!data) {
          throw new Error('No data returned after insert');
        }

        return this.transformToLLMConfig(data);
      }
    } catch (error) {
      console.error('LLMConfigService: Error saving config:', error);
      throw error;
    }
  }

  /**
   * Delete an LLM configuration
   */
  static async deleteLLMConfig(id: string): Promise<void> {
    try {
      const { error } = await supabase
        .from('llm_source_configurations')
        .delete()
        .eq('id', id);

      if (error) {
        throw new Error(`Failed to delete LLM config: ${error.message}`);
      }
    } catch (error) {
      console.error('LLMConfigService: Error deleting config:', error);
      throw error;
    }
  }

  /**
   * Transform database record to LLMConfig interface
   */
  private static transformToLLMConfig(data: any): LLMConfig {
    return {
      id: data.id,
      brandId: data.brand_id,
      source: data.source,
      technicalSource: data.technical_source || undefined,
      provider: data.provider,
      model: data.model,
      isActive: data.is_active,
      priority: data.priority,
      metadata: data.metadata || {},
      createdAt: data.created_at,
      updatedAt: data.updated_at
    };
  }
}

